import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  MenuItem,
  Chip,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Snackbar,
  CircularProgress,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  Work as WorkIcon,
  Language as LanguageIcon,
  Computer as ComputerIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { tr as trLocale, enUS } from 'date-fns/locale';
import { CVFormData, Education, Experience, Language, ComputerSkill, Reference, Certification } from '../../types/cv.types';
import { validateCV, validateTCKN, validatePhone, validateEmail } from '../../utils/cvValidation';
import cvService from '../../services/cvService';
import { useTranslation } from 'react-i18next';

const initialFormData: CVFormData = {
  personalInfo: {
    firstName: '',
    lastName: '',
    tckn: '',
    birthDate: null,
    birthPlace: '',
    gender: '',
    bloodType: '',
    nationality: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    maritalStatus: 'single',
    drivingLicense: [],
    homePhone: '',
    workPhone: '',
    emergencyContactName: '',
    emergencyContactPhone: ''
  },
  spouseInfo: {
    name: '',
    surname: '',
    occupation: '',
    phone: ''
  },
  howDidYouHear: '',
  educations: [],
  experiences: [],
  languages: [],
  computerSkills: [],
  certifications: [],
  militaryStatus: {
    status: 'completed',
    completionDate: null,
    postponeDate: null,
    exemptionReason: ''
  },
  references: [],
  physicalAttributes: {
    height: undefined,
    weight: undefined,
    bloodType: '',
    shoeSize: '',
    clothingSize: ''
  },
  jobPreferences: {
    desiredPosition: '',
    desiredSalary: undefined,
    canWorkNightShift: false,
    canTravel: false,
    canRelocate: false,
    startDate: null,
    canWorkOvertime: false,
    securityCardExpiryDate: null
  },
  specialInfo: {
    previousApplication: false,
    driverLicense: '',
    hasHealthIssue: false,
    healthIssueDetails: '',
    travelRestriction: false,
    travelRestrictionDetails: '',
    criminalRecord: false,
    criminalRecordDetails: '',
    smoker: false
  },
  additionalInfo: {
    hobbiesAndInterests: '',
    otherInfo: ''
  },
  attachments: []
};

const CVApplicationForm: React.FC = () => {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language?.toLowerCase().startsWith('en');
  const tr = (trText: string, enText: string) => (isEnglish ? enText : trText);

  const [formData, setFormData] = useState<CVFormData>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [expanded, setExpanded] = useState<string>('panel1');
  
  // Checkbox states for "Yok" options
  const [noEducation, setNoEducation] = useState(false);
  const [noExperience, setNoExperience] = useState(false);
  const [noLanguage, setNoLanguage] = useState(false);
  const [noComputerSkill, setNoComputerSkill] = useState(false);
  const [noReference, setNoReference] = useState(false);

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : '');
  };

  // Personal Info handlers
  const handlePersonalInfoChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      personalInfo: {
        ...prev.personalInfo,
        [field]: value
      }
    }));
    // Clear error for this field
    if (errors[`personalInfo.${field}`]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`personalInfo.${field}`];
        return newErrors;
      });
    }
  };

  // Education handlers
  const addEducation = () => {
    setFormData(prev => ({
      ...prev,
      educations: [...prev.educations, {
        id: Date.now().toString(),
        schoolName: '',
        department: '',
        degree: 'bachelor',
        startYear: new Date().getFullYear(),
        endYear: new Date().getFullYear(),
        isGraduated: false
      }]
    }));
  };

  const updateEducation = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      educations: prev.educations.map((edu, i) => 
        i === index ? { ...edu, [field]: value } : edu
      )
    }));
  };

  const removeEducation = (index: number) => {
    setFormData(prev => ({
      ...prev,
      educations: prev.educations.filter((_, i) => i !== index)
    }));
  };

  // Experience handlers
  const addExperience = () => {
    setFormData(prev => ({
      ...prev,
      experiences: [...prev.experiences, {
        id: Date.now().toString(),
        companyName: '',
        position: '',
        startDate: null,
        endDate: null,
        isCurrentJob: false,
        description: '',
        leavingReason: ''
      }]
    }));
  };

  const updateExperience = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      experiences: prev.experiences.map((exp, i) => 
        i === index ? { ...exp, [field]: value } : exp
      )
    }));
  };

  const removeExperience = (index: number) => {
    setFormData(prev => ({
      ...prev,
      experiences: prev.experiences.filter((_, i) => i !== index)
    }));
  };

  // Language handlers
  const addLanguage = () => {
    setFormData(prev => ({
      ...prev,
      languages: [...prev.languages, {
        id: Date.now().toString(),
        name: '',
        level: 'intermediate',
        hasCertificate: false
      }]
    }));
  };

  const updateLanguage = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      languages: prev.languages.map((lang, i) => 
        i === index ? { ...lang, [field]: value } : lang
      )
    }));
  };

  const removeLanguage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      languages: prev.languages.filter((_, i) => i !== index)
    }));
  };

  // Computer Skill handlers
  const addComputerSkill = () => {
    setFormData(prev => ({
      ...prev,
      computerSkills: [...prev.computerSkills, {
        id: Date.now().toString(),
        name: '',
        level: 'intermediate'
      }]
    }));
  };

  const updateComputerSkill = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      computerSkills: prev.computerSkills.map((skill, i) => 
        i === index ? { ...skill, [field]: value } : skill
      )
    }));
  };

  const removeComputerSkill = (index: number) => {
    setFormData(prev => ({
      ...prev,
      computerSkills: prev.computerSkills.filter((_, i) => i !== index)
    }));
  };

  // Reference handlers
  const addReference = () => {
    setFormData(prev => ({
      ...prev,
      references: [...prev.references, {
        id: Date.now().toString(),
        fullName: '',
        company: '',
        position: '',
        phone: '',
        email: ''
      }]
    }));
  };

  const updateReference = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      references: prev.references.map((ref, i) => 
        i === index ? { ...ref, [field]: value } : ref
      )
    }));
  };

  const removeReference = (index: number) => {
    setFormData(prev => ({
      ...prev,
      references: prev.references.filter((_, i) => i !== index)
    }));
  };

  // Certification handlers
  const addCertification = () => {
    setFormData(prev => ({
      ...prev,
      certifications: [...(prev.certifications || []), {
        id: Date.now().toString(),
        institution: '',
        subject: '',
        durationHours: undefined,
        certificationDate: null
      }]
    }));
  };

  const updateCertification = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      certifications: (prev.certifications || []).map((cert, i) => 
        i === index ? { ...cert, [field]: value } : cert
      )
    }));
  };

  const removeCertification = (index: number) => {
    setFormData(prev => ({
      ...prev,
      certifications: (prev.certifications || []).filter((_, i) => i !== index)
    }));
  };

  // Form submission
  const handleSubmit = async () => {
    try {
      setLoading(true);
      setErrors({});

      // Frontend validation (temel kontroller)
      const validation = validateCV(formData);
      if (!validation.isValid) {
        setErrors(validation.errors);
        setSnackbar({
          open: true,
          message: tr('Lütfen tüm zorunlu alanları doldurun ve hataları düzeltin.', 'Please fill in all required fields and fix errors.'),
          severity: 'error'
        });
        setLoading(false);
        return;
      }

      // Eğitim kontrolü: Hiç eğitim yoksa "Yok" checkbox'ı işaretlenmeli
      if (formData.educations.length === 0 && !noEducation) {
        setSnackbar({
          open: true,
          message: tr('Lütfen en az bir eğitim bilgisi girin veya "Eğitim bilgim bulunmamaktadır" seçeneğini işaretleyin.', 'Please enter at least one education record or check "I do not have education information".'),
          severity: 'error'
        });
        setLoading(false);
        return;
      }

      // Deneyim kontrolü: Hiç deneyim yoksa "Yok" checkbox'ı işaretlenmeli
      if (formData.experiences.length === 0 && !noExperience) {
        setSnackbar({
          open: true,
          message: tr('Lütfen en az bir iş deneyimi girin veya "İş deneyimim bulunmamaktadır" seçeneğini işaretleyin.', 'Please enter at least one work experience or check "I do not have work experience".'),
          severity: 'error'
        });
        setLoading(false);
        return;
      }

      console.log('📋 Form Data (Frontend State):', formData);

      // Submit to backend - cvService.ts zaten doğru mapping yapıyor
      const result = await cvService.createCV(formData);
      
      console.log('✅ Backend Response:', result);
      
      setSnackbar({
        open: true,
        message: tr('CV başvurunuz başarıyla kaydedildi!', 'Your CV application has been saved successfully!'),
        severity: 'success'
      });

      // Reset form after 2 seconds
      setTimeout(() => {
        setFormData(initialFormData);
        setNoEducation(false);
        setNoExperience(false);
        setNoLanguage(false);
        setNoComputerSkill(false);
        setNoReference(false);
        setExpanded('panel1');
      }, 2000);

    } catch (error: any) {
      console.error('❌ CV submission error:', error);
      
      // Backend validation hatalarını kullanıcıya göster
      let errorMessage = tr('CV kaydedilirken bir hata oluştu.', 'An error occurred while saving the CV.');
      let errorDetails: string[] = [];
      
      if (error.response?.data?.errors) {
        const validationErrors = error.response.data.errors;
        
        console.error('🔍 Backend Validation Hataları:', validationErrors);
        
        // Eğer errors array ise
        if (Array.isArray(validationErrors)) {
          errorDetails = validationErrors;
          errorMessage = tr('Backend hataları:\n', 'Backend errors:\n') + validationErrors.map((e, i) => `${i + 1}. ${e}`).join('\n');
        } 
        // Eğer errors object ise (field: message)
        else if (typeof validationErrors === 'object') {
          const errorFields = Object.keys(validationErrors);
          if (errorFields.length > 0) {
            errorDetails = errorFields.map(field => {
              const errorValue = validationErrors[field];
              const errorText = Array.isArray(errorValue) 
                ? errorValue.join(', ') 
                : String(errorValue);
              return `${field}: ${errorText}`;
            });
            errorMessage = tr('Lütfen şu alanları kontrol edin:\n', 'Please check the following fields:\n') + 
              errorDetails.map((e, i) => `${i + 1}. ${e}`).join('\n');
          }
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.title) {
        errorMessage = error.response.data.title;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      console.error('📍 Error Message:', errorMessage);
      console.error('📍 Error Details:', errorDetails);
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100vh', 
      backgroundColor: '#f5f5f5',
      overflowX: 'hidden',
      overflowY: 'auto',
      py: { xs: 2, sm: 4 }
    }}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={isEnglish ? enUS : trLocale}>
        <Box sx={{ px: { xs: 2, sm: 3 }, maxWidth: 1200, margin: '0 auto' }}>
          {/* Header Card */}
          <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', boxShadow: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', color: 'white' }}>
                <DescriptionIcon sx={{ fontSize: { xs: 36, sm: 48 }, mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="h1" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                    {tr('CV Oluşturma / İş Başvuru Formu', 'CV Creation / Job Application Form')}
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1, opacity: 0.9, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {tr('Lütfen tüm bilgilerinizi eksiksiz ve doğru şekilde doldurunuz.', 'Please fill in all your information completely and accurately.')}
                  </Typography>
                </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Personal Information */}
        <Accordion expanded={expanded === 'panel1'} onChange={handleAccordionChange('panel1')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <PersonIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h6">{tr('Kişisel Bilgiler', 'Personal Information')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label={tr('Ad', 'First Name')}
                  value={formData.personalInfo.firstName}
                  onChange={(e) => handlePersonalInfoChange('firstName', e.target.value)}
                  error={!!errors['personalInfo.firstName']}
                  helperText={errors['personalInfo.firstName']}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label={tr('Soyad', 'Last Name')}
                  value={formData.personalInfo.lastName}
                  onChange={(e) => handlePersonalInfoChange('lastName', e.target.value)}
                  error={!!errors['personalInfo.lastName']}
                  helperText={errors['personalInfo.lastName']}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label={tr('TC Kimlik No', 'National ID No')}
                  value={formData.personalInfo.tckn}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 11);
                    handlePersonalInfoChange('tckn', value);
                  }}
                  error={!!errors['personalInfo.tckn']}
                  helperText={errors['personalInfo.tckn']}
                  inputProps={{ maxLength: 11 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label={tr('Doğum Tarihi *', 'Birth Date *')}
                  value={formData.personalInfo.birthDate}
                  onChange={(date) => handlePersonalInfoChange('birthDate', date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors['personalInfo.birthDate'],
                      helperText: errors['personalInfo.birthDate']
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={tr('Doğum Yeri', 'Birth Place')}
                  value={formData.personalInfo.birthPlace || ''}
                  onChange={(e) => handlePersonalInfoChange('birthPlace', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{tr('Cinsiyet', 'Gender')}</InputLabel>
                  <Select
                    value={formData.personalInfo.gender || ''}
                    label={tr('Cinsiyet', 'Gender')}
                    onChange={(e) => handlePersonalInfoChange('gender', e.target.value)}
                  >
                    <MenuItem value="">{tr('Seçiniz', 'Select')}</MenuItem>
                    <MenuItem value="Erkek">{tr('Erkek', 'Male')}</MenuItem>
                    <MenuItem value="Kadın">{tr('Kadın', 'Female')}</MenuItem>
                    <MenuItem value="Belirtmek İstemiyorum">{tr('Belirtmek İstemiyorum', 'Prefer Not to Say')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{tr('Kan Grubu', 'Blood Type')}</InputLabel>
                  <Select
                    value={formData.personalInfo.bloodType || ''}
                    label={tr('Kan Grubu', 'Blood Type')}
                    onChange={(e) => handlePersonalInfoChange('bloodType', e.target.value)}
                  >
                    <MenuItem value="">{tr('Seçiniz', 'Select')}</MenuItem>
                    <MenuItem value="A+">A Rh+</MenuItem>
                    <MenuItem value="A-">A Rh-</MenuItem>
                    <MenuItem value="B+">B Rh+</MenuItem>
                    <MenuItem value="B-">B Rh-</MenuItem>
                    <MenuItem value="AB+">AB Rh+</MenuItem>
                    <MenuItem value="AB-">AB Rh-</MenuItem>
                    <MenuItem value="0+">0 Rh+</MenuItem>
                    <MenuItem value="0-">0 Rh-</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={tr('Uyruk', 'Nationality')}
                  value={formData.personalInfo.nationality || (isEnglish ? 'Turkey' : 'Türkiye')}
                  onChange={(e) => handlePersonalInfoChange('nationality', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{tr('Medeni Durum', 'Marital Status')}</InputLabel>
                  <Select
                    value={formData.personalInfo.maritalStatus}
                    label={tr('Medeni Durum', 'Marital Status')}
                    onChange={(e) => handlePersonalInfoChange('maritalStatus', e.target.value)}
                  >
                    <MenuItem value="single">{tr('Bekar', 'Single')}</MenuItem>
                    <MenuItem value="married">{tr('Evli', 'Married')}</MenuItem>
                    <MenuItem value="divorced">{tr('Boşanmış', 'Divorced')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label={tr('Telefon', 'Phone')}
                  placeholder={tr('0555 555 55 55', '0555 555 55 55')}
                  value={formData.personalInfo.phone}
                  onChange={(e) => handlePersonalInfoChange('phone', e.target.value)}
                  error={!!errors['personalInfo.phone']}
                  helperText={errors['personalInfo.phone']}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  type="email"
                  label={tr('E-posta', 'Email')}
                  value={formData.personalInfo.email}
                  onChange={(e) => handlePersonalInfoChange('email', e.target.value)}
                  error={!!errors['personalInfo.email']}
                  helperText={errors['personalInfo.email']}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label={tr('Adres', 'Address')}
                  multiline
                  rows={2}
                  value={formData.personalInfo.address}
                  onChange={(e) => handlePersonalInfoChange('address', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={tr('Şehir', 'City')}
                  value={formData.personalInfo.city}
                  onChange={(e) => handlePersonalInfoChange('city', e.target.value)}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Education */}
        <Accordion expanded={expanded === 'panel2'} onChange={handleAccordionChange('panel2')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <SchoolIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h6">{tr('Eğitim Bilgileri', 'Education')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              {/* "Eğitim bilgim yok" checkbox */}
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={noEducation}
                      onChange={(e) => {
                        setNoEducation(e.target.checked);
                        if (e.target.checked) {
                          // Eğer "Yok" işaretlenmişse tüm eğitimleri temizle
                          setFormData(prev => ({ ...prev, educations: [] }));
                        }
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" color="error">
                      {tr('⚠️ Eğitim bilgim bulunmamaktadır (Bu seçeneği işaretlerseniz eğitim bilgisi ekleyemezsiniz)', '⚠️ I do not have education information (If selected, you cannot add education records)')}
                    </Typography>
                  }
                />
              </Box>

              {!noEducation && formData.educations.map((education, index) => (
                <Card key={education.id} sx={{ mb: 2, bgcolor: 'grey.50' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {tr('Eğitim', 'Education')} #{index + 1}
                      </Typography>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeEducation(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          required
                          label={tr('Okul Adı', 'School Name')}
                          value={education.schoolName}
                          onChange={(e) => updateEducation(index, 'schoolName', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          required
                          label={tr('Bölüm', 'Department')}
                          value={education.department}
                          onChange={(e) => updateEducation(index, 'department', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth required>
                          <InputLabel>{tr('Derece', 'Degree')}</InputLabel>
                          <Select
                            value={education.degree}
                            label={tr('Derece', 'Degree')}
                            onChange={(e) => updateEducation(index, 'degree', e.target.value)}
                          >
                            <MenuItem value="primary">{tr('İlköğretim', 'Primary School')}</MenuItem>
                            <MenuItem value="highschool">{tr('Lise', 'High School')}</MenuItem>
                            <MenuItem value="associate">{tr('Ön Lisans', 'Associate Degree')}</MenuItem>
                            <MenuItem value="bachelor">{tr('Lisans', 'Bachelor')}</MenuItem>
                            <MenuItem value="master">{tr('Yüksek Lisans', 'Master')}</MenuItem>
                            <MenuItem value="doctorate">{tr('Doktora', 'Doctorate')}</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          type="number"
                          label={tr('Başlangıç Yılı', 'Start Year')}
                          value={education.startYear}
                          onChange={(e) => updateEducation(index, 'startYear', parseInt(e.target.value))}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          type="number"
                          label={tr('Bitiş Yılı', 'End Year')}
                          value={education.endYear}
                          onChange={(e) => updateEducation(index, 'endYear', parseInt(e.target.value))}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          type="number"
                          label={tr('Not Ortalaması (GPA)', 'Grade Point Average (GPA)')}
                          value={education.gpa || ''}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value);
                            if (!isNaN(value) && value <= 3.99) {
                              updateEducation(index, 'gpa', value);
                            } else if (e.target.value === '') {
                              updateEducation(index, 'gpa', undefined);
                            }
                          }}
                          inputProps={{ 
                            max: 3.99, 
                            step: 0.01,
                            min: 0
                          }}
                          helperText={tr('Max: 3.99', 'Max: 3.99')}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={education.isGraduated}
                              onChange={(e) => updateEducation(index, 'isGraduated', e.target.checked)}
                            />
                          }
                          label={tr('Mezun Oldum', 'Graduated')}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
              
              {!noEducation && (
                <Button
                  startIcon={<AddIcon />}
                  variant="outlined"
                  onClick={addEducation}
                  fullWidth
                >
                  {tr('Eğitim Ekle', 'Add Education')}
                </Button>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Experience */}
        <Accordion expanded={expanded === 'panel3'} onChange={handleAccordionChange('panel3')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <WorkIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h6">{tr('İş Deneyimi', 'Work Experience')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              {/* "İş deneyimim yok" checkbox */}
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={noExperience}
                      onChange={(e) => {
                        setNoExperience(e.target.checked);
                        if (e.target.checked) {
                          // Eğer "Yok" işaretlenmişse tüm deneyimleri temizle
                          setFormData(prev => ({ ...prev, experiences: [] }));
                        }
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" color="error">
                      {tr('⚠️ İş deneyimim bulunmamaktadır (Bu seçeneği işaretlerseniz deneyim ekleyemezsiniz)', '⚠️ I do not have work experience (If selected, you cannot add experience records)')}
                    </Typography>
                  }
                />
              </Box>

              {!noExperience && formData.experiences.map((experience, index) => (
                <Card key={experience.id} sx={{ mb: 2, bgcolor: 'grey.50' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {tr('Deneyim', 'Experience')} #{index + 1}
                      </Typography>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeExperience(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          required
                          label={tr('Şirket Adı', 'Company Name')}
                          value={experience.companyName}
                          onChange={(e) => updateExperience(index, 'companyName', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          required
                          label={tr('Pozisyon', 'Position')}
                          value={experience.position}
                          onChange={(e) => updateExperience(index, 'position', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <DatePicker
                          label={tr('Başlangıç Tarihi *', 'Start Date *')}
                          value={experience.startDate}
                          onChange={(date) => updateExperience(index, 'startDate', date)}
                          slotProps={{ textField: { fullWidth: true, required: true } }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <DatePicker
                          label={tr('Bitiş Tarihi', 'End Date')}
                          value={experience.endDate}
                          onChange={(date) => updateExperience(index, 'endDate', date)}
                          disabled={experience.isCurrentJob}
                          slotProps={{ textField: { fullWidth: true } }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label={tr('Net Ücret (TL)', 'Net Salary (TRY)')}
                          value={experience.netSalary || ''}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value);
                            // Maksimum 999,999,999 TL
                            if (!isNaN(value) && value > 999999999) {
                              return;
                            }
                            updateExperience(index, 'netSalary', value);
                          }}
                          inputProps={{ 
                            min: 0, 
                            max: 999999999,
                            step: 100 
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label={tr('Ayrılış Sebebi', 'Reason for Leaving')}
                          value={experience.leavingReason || ''}
                          onChange={(e) => updateExperience(index, 'leavingReason', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label={tr('İş Tanımı / Göreviniz', 'Job Description / Duties')}
                          value={experience.description}
                          onChange={(e) => updateExperience(index, 'description', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={experience.isCurrentJob}
                              onChange={(e) => updateExperience(index, 'isCurrentJob', e.target.checked)}
                            />
                          }
                          label={tr('Halen bu pozisyonda çalışıyorum', 'I currently work in this position')}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
              
              {!noExperience && (
                <Button
                  startIcon={<AddIcon />}
                  variant="outlined"
                  onClick={addExperience}
                  fullWidth
                >
                  {tr('İş Deneyimi Ekle', 'Add Work Experience')}
                </Button>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Languages & Computer Skills */}
        <Accordion expanded={expanded === 'panel4'} onChange={handleAccordionChange('panel4')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <LanguageIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h6">{tr('Yetkinlikler', 'Competencies')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {tr('Yabancı Diller', 'Foreign Languages')}
                </Typography>
                {formData.languages.map((language, index) => (
                  <Card key={language.id} sx={{ mb: 2, bgcolor: 'grey.50' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">{tr('Dil', 'Language')} #{index + 1}</Typography>
                        <IconButton size="small" color="error" onClick={() => removeLanguage(index)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <TextField
                        fullWidth
                        size="small"
                        label={tr('Dil', 'Language')}
                        value={language.name}
                        onChange={(e) => updateLanguage(index, 'name', e.target.value)}
                        sx={{ mb: 1 }}
                      />
                      <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                        <InputLabel>{tr('Seviye', 'Level')}</InputLabel>
                        <Select
                          value={language.level}
                          label={tr('Seviye', 'Level')}
                          onChange={(e) => updateLanguage(index, 'level', e.target.value)}
                        >
                          <MenuItem value="beginner">{tr('Başlangıç', 'Beginner')}</MenuItem>
                          <MenuItem value="intermediate">{tr('Orta', 'Intermediate')}</MenuItem>
                          <MenuItem value="advanced">{tr('İleri', 'Advanced')}</MenuItem>
                          <MenuItem value="native">{tr('Ana Dil', 'Native')}</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        fullWidth
                        size="small"
                        label={tr('Öğrenilen Kurum', 'Institution')}
                        value={language.institution || ''}
                        onChange={(e) => updateLanguage(index, 'institution', e.target.value)}
                        sx={{ mb: 1 }}
                      />
                      <DatePicker
                        label={tr('Tarih', 'Date')}
                        value={language.certificateDate || null}
                        onChange={(date) => updateLanguage(index, 'certificateDate', date)}
                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                      />
                    </CardContent>
                  </Card>
                ))}
                <Button startIcon={<AddIcon />} size="small" onClick={addLanguage}>
                  {tr('Dil Ekle', 'Add Language')}
                </Button>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {tr('Bilgisayar Becerileri', 'Computer Skills')}
                </Typography>
                {formData.computerSkills.map((skill, index) => (
                  <Card key={skill.id} sx={{ mb: 2, bgcolor: 'grey.50' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">{tr('Beceri', 'Skill')} #{index + 1}</Typography>
                        <IconButton size="small" color="error" onClick={() => removeComputerSkill(index)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <TextField
                        fullWidth
                        size="small"
                        label={tr('Program/Teknoloji', 'Program/Technology')}
                        value={skill.name}
                        onChange={(e) => updateComputerSkill(index, 'name', e.target.value)}
                        sx={{ mb: 1 }}
                      />
                      <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                        <InputLabel>{tr('Seviye', 'Level')}</InputLabel>
                        <Select
                          value={skill.level}
                          label={tr('Seviye', 'Level')}
                          onChange={(e) => updateComputerSkill(index, 'level', e.target.value)}
                        >
                          <MenuItem value="beginner">{tr('Başlangıç', 'Beginner')}</MenuItem>
                          <MenuItem value="intermediate">{tr('Orta', 'Intermediate')}</MenuItem>
                          <MenuItem value="advanced">{tr('İleri', 'Advanced')}</MenuItem>
                          <MenuItem value="expert">{tr('Uzman', 'Expert')}</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        fullWidth
                        size="small"
                        label={tr('Öğrenilen Kurum', 'Institution')}
                        value={skill.institution || ''}
                        onChange={(e) => updateComputerSkill(index, 'institution', e.target.value)}
                        sx={{ mb: 1 }}
                      />
                      <DatePicker
                        label={tr('Tarih', 'Date')}
                        value={skill.certificateDate || null}
                        onChange={(date) => updateComputerSkill(index, 'certificateDate', date)}
                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                      />
                    </CardContent>
                  </Card>
                ))}
                <Button startIcon={<AddIcon />} size="small" onClick={addComputerSkill}>
                  {tr('Beceri Ekle', 'Add Skill')}
                </Button>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Job Preferences */}
        <Accordion expanded={expanded === 'panel5'} onChange={handleAccordionChange('panel5')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <WorkIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h6">{tr('İş Tercihleri', 'Job Preferences')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>{tr('Talep Edilen Pozisyon', 'Desired Position')}</InputLabel>
                  <Select
                    value={formData.jobPreferences.desiredPosition}
                    label={tr('Talep Edilen Pozisyon', 'Desired Position')}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      jobPreferences: { ...prev.jobPreferences, desiredPosition: e.target.value }
                    }))}
                  >
                    <MenuItem value="">{tr('Seçiniz', 'Select')}</MenuItem>
                    <MenuItem value="Güvenlik">{tr('Güvenlik', 'Security')}</MenuItem>
                    <MenuItem value="Temizlik">{tr('Temizlik', 'Cleaning')}</MenuItem>
                    <MenuItem value="Danışma Personeli">{tr('Danışma Personeli', 'Reception Staff')}</MenuItem>
                    <MenuItem value="Üretim İşçisi">{tr('Üretim İşçisi', 'Production Worker')}</MenuItem>
                    <MenuItem value="Diğer">{tr('Diğer', 'Other')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Güvenlik Kartı Bitiş Tarihi - Sadece Güvenlik pozisyonu seçilirse göster */}
              {formData.jobPreferences.desiredPosition === 'Güvenlik' && (
                <Grid item xs={12} md={6}>
                  <DatePicker
                    label={tr('Güvenlik Kartı Bitiş Tarihi', 'Security Card Expiry Date')}
                    value={formData.jobPreferences.securityCardExpiryDate}
                    onChange={(date) => setFormData(prev => ({
                      ...prev,
                      jobPreferences: { ...prev.jobPreferences, securityCardExpiryDate: date }
                    }))}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        helperText: tr('Güvenlik pozisyonu için güvenlik kartı bitiş tarihi gereklidir', 'Security card expiry date is required for security position')
                      }
                    }}
                  />
                </Grid>
              )}
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={tr('Talep Edilen Ücret (TL)', 'Desired Salary (TRY)')}
                  value={formData.jobPreferences.desiredSalary || ''}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    // Maksimum 999,999,999 TL (1 milyar altı)
                    if (!isNaN(value) && value > 999999999) {
                      return; // Çok büyük değer, güncellemeyi durdur
                    }
                    setFormData(prev => ({
                      ...prev,
                      jobPreferences: { ...prev.jobPreferences, desiredSalary: value }
                    }));
                  }}
                  inputProps={{ 
                    min: 0, 
                    max: 999999999,
                    step: 100 
                  }}
                  helperText={tr('Maksimum: 999,999,999 TL', 'Maximum: 999,999,999 TRY')}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <DatePicker
                  label={tr('İşe Başlayabileceğiniz Tarih', 'Available Start Date')}
                  value={formData.jobPreferences.startDate}
                  onChange={(date) => setFormData(prev => ({
                    ...prev,
                    jobPreferences: { ...prev.jobPreferences, startDate: date }
                  }))}
                  slotProps={{
                    textField: {
                      fullWidth: true
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 1 }}>
                  {tr('Çalışma Koşulları Tercihleri', 'Work Condition Preferences')}
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.jobPreferences.canWorkNightShift || false}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        jobPreferences: { ...prev.jobPreferences, canWorkNightShift: e.target.checked }
                      }))}
                    />
                  }
                  label={tr('Gece vardiyasında çalışabilirim', 'I can work night shifts')}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.jobPreferences.canWorkOvertime || false}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        jobPreferences: { ...prev.jobPreferences, canWorkOvertime: e.target.checked }
                      }))}
                    />
                  }
                  label={tr('Fazla mesai yapabilirim', 'I can work overtime')}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.jobPreferences.canTravel || false}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        jobPreferences: { ...prev.jobPreferences, canTravel: e.target.checked }
                      }))}
                    />
                  }
                  label={tr('Seyahat edebilirim', 'I can travel')}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.jobPreferences.canRelocate || false}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        jobPreferences: { ...prev.jobPreferences, canRelocate: e.target.checked }
                      }))}
                    />
                  }
                  label={tr('Şehir değişikliğine açığım', 'I am open to relocation')}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Spouse Info & How Did You Hear */}
        <Accordion expanded={expanded === 'panel6'} onChange={handleAccordionChange('panel6')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <PersonIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h6">{tr('Aile Bilgileri ve Diğer', 'Family Information and Other')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {formData.personalInfo.maritalStatus === 'married' && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" color="primary" gutterBottom>
                      {tr('Eş Bilgileri', 'Spouse Information')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={tr('Eşinizin Adı', 'Spouse First Name')}
                      value={formData.spouseInfo?.name || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        spouseInfo: { ...prev.spouseInfo, name: e.target.value }
                      }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={tr('Eşinizin Soyadı', 'Spouse Last Name')}
                      value={formData.spouseInfo?.surname || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        spouseInfo: { ...prev.spouseInfo, surname: e.target.value }
                      }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={tr('Eşinizin Mesleği', 'Spouse Occupation')}
                      value={formData.spouseInfo?.occupation || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        spouseInfo: { ...prev.spouseInfo, occupation: e.target.value }
                      }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={tr('Eşinizin Telefonu', 'Spouse Phone')}
                      value={formData.spouseInfo?.phone || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        spouseInfo: { ...prev.spouseInfo, phone: e.target.value }
                      }))}
                    />
                  </Grid>
                </>
              )}
              
              <Grid item xs={12}>
                <Typography variant="subtitle1" color="primary" gutterBottom sx={{ mt: 2 }}>
                  {tr('Firmamıza Nasıl Ulaştınız?', 'How Did You Hear About Our Company?')}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>{tr('Seçiniz', 'Select')}</InputLabel>
                  <Select
                    value={formData.howDidYouHear || ''}
                    label={tr('Seçiniz', 'Select')}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      howDidYouHear: e.target.value as any
                    }))}
                  >
                    <MenuItem value="iskur">{tr('İŞKUR Kanalı ile', 'Via İŞKUR')}</MenuItem>
                    <MenuItem value="employment_office">{tr('İstihdam Ofisi', 'Employment Office')}</MenuItem>
                    <MenuItem value="eleman_net">{tr('Eleman.net', 'Eleman.net')}</MenuItem>
                    <MenuItem value="social_media">{tr('Sosyal Medya', 'Social Media')}</MenuItem>
                    <MenuItem value="employee_referral">{tr('Firmada Çalışan Yönlendirmesi', 'Employee Referral')}</MenuItem>
                    <MenuItem value="friend_recommendation">{tr('Arkadaş Önerisi', 'Friend Recommendation')}</MenuItem>
                    <MenuItem value="job_posting">{tr('Yapıştırma İlanlardan', 'From Job Postings')}</MenuItem>
                    <MenuItem value="by_chance">{tr('Tesadüfen', 'By Chance')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Certifications */}
        <Accordion expanded={expanded === 'panel7'} onChange={handleAccordionChange('panel7')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <SchoolIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h6">{tr('Katıldığınız Kurs/Seminer/Eğitimler (Sertifikalı)', 'Courses/Seminars/Trainings Attended (Certified)')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {(formData.certifications || []).map((cert, index) => (
                  <Card key={cert.id} sx={{ mb: 2, p: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle2" color="primary">
                          {tr('Eğitim', 'Training')} #{index + 1}
                        </Typography>
                        <IconButton onClick={() => removeCertification(index)} size="small" color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label={tr('Eğitim Veren Kuruluş', 'Training Provider')}
                            value={cert.institution}
                            onChange={(e) => updateCertification(index, 'institution', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label={tr('Eğitimin Konusu', 'Training Subject')}
                            value={cert.subject}
                            onChange={(e) => updateCertification(index, 'subject', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            type="number"
                            label={tr('Süre (Saat Olarak)', 'Duration (Hours)')}
                            value={cert.durationHours || ''}
                            onChange={(e) => updateCertification(index, 'durationHours', parseInt(e.target.value))}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <DatePicker
                            label={tr('Eğitim Tarihi', 'Training Date')}
                            value={cert.certificationDate}
                            onChange={(date) => updateCertification(index, 'certificationDate', date)}
                            slotProps={{ textField: { fullWidth: true } }}
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
                <Button startIcon={<AddIcon />} onClick={addCertification} variant="outlined">
                  {tr('Eğitim Ekle', 'Add Training')}
                </Button>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Military Status */}
        <Accordion expanded={expanded === 'panel8'} onChange={handleAccordionChange('panel8')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <PersonIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h6">{tr('Askerlik Durumu', 'Military Status')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{tr('Askerlik Durumu', 'Military Status')}</InputLabel>
                  <Select
                    value={formData.militaryStatus.status}
                    label={tr('Askerlik Durumu', 'Military Status')}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      militaryStatus: { ...prev.militaryStatus, status: e.target.value as any }
                    }))}
                  >
                    <MenuItem value="completed">{tr('Yapıldı', 'Completed')}</MenuItem>
                    <MenuItem value="postponed">{tr('Tecilli', 'Postponed')}</MenuItem>
                    <MenuItem value="exempt">{tr('Muaf', 'Exempt')}</MenuItem>
                    <MenuItem value="not_applicable">{tr('Muaf (Kadın)', 'Exempt (Female)')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {formData.militaryStatus.status === 'completed' && (
                <Grid item xs={12} md={6}>
                  <DatePicker
                    label={tr('Terhis Tarihi', 'Discharge Date')}
                    value={formData.militaryStatus.completionDate}
                    onChange={(date) => setFormData(prev => ({
                      ...prev,
                      militaryStatus: { ...prev.militaryStatus, completionDate: date }
                    }))}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
              )}
              {formData.militaryStatus.status === 'postponed' && (
                <Grid item xs={12} md={6}>
                  <DatePicker
                    label={tr('Tecil Tarihi', 'Postponement Date')}
                    value={formData.militaryStatus.postponeDate}
                    onChange={(date) => setFormData(prev => ({
                      ...prev,
                      militaryStatus: { ...prev.militaryStatus, postponeDate: date }
                    }))}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
              )}
              {formData.militaryStatus.status === 'exempt' && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={tr('Muafiyet Nedeni', 'Exemption Reason')}
                    value={formData.militaryStatus.exemptionReason || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      militaryStatus: { ...prev.militaryStatus, exemptionReason: e.target.value }
                    }))}
                  />
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Special Information */}
        <Accordion expanded={expanded === 'panel9'} onChange={handleAccordionChange('panel9')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <DescriptionIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h6">{tr('Özel Bilgiler', 'Special Information')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  {tr('Şirketimize Daha Önce Başvuruda Bulundunuz mu?', 'Have You Applied to Our Company Before?')}
                </Typography>
                <FormControl component="fieldset">
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Chip
                      label={tr('Evet', 'Yes')}
                      color={formData.specialInfo?.previousApplication ? 'primary' : 'default'}
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        specialInfo: { ...prev.specialInfo, previousApplication: true }
                      }))}
                      sx={{ cursor: 'pointer' }}
                    />
                    <Chip
                      label={tr('Hayır', 'No')}
                      color={!formData.specialInfo?.previousApplication ? 'primary' : 'default'}
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        specialInfo: { ...prev.specialInfo, previousApplication: false }
                      }))}
                      sx={{ cursor: 'pointer' }}
                    />
                  </Box>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  {tr('Sürücü Belgeniz Var mı? Varsa Sınıfını Belirtiniz.', 'Do You Have a Driver License? If yes, specify class.')}
                </Typography>
                <TextField
                  fullWidth
                  placeholder={tr('Örn: B, C, D', 'Ex: B, C, D')}
                  value={formData.specialInfo?.driverLicense || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    specialInfo: { ...prev.specialInfo, driverLicense: e.target.value }
                  }))}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  {tr('Sağlık Sorununuz Var mı?', 'Do You Have Any Health Issues?')}
                </Typography>
                <FormControl component="fieldset">
                  <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                    <Chip
                      label={tr('Evet', 'Yes')}
                      color={formData.specialInfo?.hasHealthIssue ? 'primary' : 'default'}
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        specialInfo: { ...prev.specialInfo, hasHealthIssue: true }
                      }))}
                      sx={{ cursor: 'pointer' }}
                    />
                    <Chip
                      label={tr('Hayır', 'No')}
                      color={!formData.specialInfo?.hasHealthIssue ? 'primary' : 'default'}
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        specialInfo: { ...prev.specialInfo, hasHealthIssue: false }
                      }))}
                      sx={{ cursor: 'pointer' }}
                    />
                  </Box>
                </FormControl>
                {formData.specialInfo?.hasHealthIssue && (
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    placeholder={tr('Açıklayınız', 'Please explain')}
                    value={formData.specialInfo?.healthIssueDetails || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      specialInfo: { ...prev.specialInfo, healthIssueDetails: e.target.value }
                    }))}
                  />
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  {tr('Seyahat Engeliniz Var mı?', 'Do You Have Any Travel Restrictions?')}
                </Typography>
                <FormControl component="fieldset">
                  <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                    <Chip
                      label={tr('Evet', 'Yes')}
                      color={formData.specialInfo?.travelRestriction ? 'primary' : 'default'}
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        specialInfo: { ...prev.specialInfo, travelRestriction: true }
                      }))}
                      sx={{ cursor: 'pointer' }}
                    />
                    <Chip
                      label={tr('Hayır', 'No')}
                      color={!formData.specialInfo?.travelRestriction ? 'primary' : 'default'}
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        specialInfo: { ...prev.specialInfo, travelRestriction: false }
                      }))}
                      sx={{ cursor: 'pointer' }}
                    />
                  </Box>
                </FormControl>
                {formData.specialInfo?.travelRestriction && (
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    placeholder={tr('Açıklayınız', 'Please explain')}
                    value={formData.specialInfo?.travelRestrictionDetails || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      specialInfo: { ...prev.specialInfo, travelRestrictionDetails: e.target.value }
                    }))}
                  />
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  {tr('Adli Sicil Kaydınız Var mı?', 'Do You Have a Criminal Record?')}
                </Typography>
                <FormControl component="fieldset">
                  <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                    <Chip
                      label={tr('Evet', 'Yes')}
                      color={formData.specialInfo?.criminalRecord ? 'primary' : 'default'}
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        specialInfo: { ...prev.specialInfo, criminalRecord: true }
                      }))}
                      sx={{ cursor: 'pointer' }}
                    />
                    <Chip
                      label={tr('Hayır', 'No')}
                      color={!formData.specialInfo?.criminalRecord ? 'primary' : 'default'}
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        specialInfo: { ...prev.specialInfo, criminalRecord: false }
                      }))}
                      sx={{ cursor: 'pointer' }}
                    />
                  </Box>
                </FormControl>
                {formData.specialInfo?.criminalRecord && (
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    placeholder={tr('Açıklayınız', 'Please explain')}
                    value={formData.specialInfo?.criminalRecordDetails || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      specialInfo: { ...prev.specialInfo, criminalRecordDetails: e.target.value }
                    }))}
                  />
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  {tr('Sigara Kullanıyor musunuz?', 'Do You Smoke?')}
                </Typography>
                <FormControl component="fieldset">
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Chip
                      label={tr('Evet', 'Yes')}
                      color={formData.specialInfo?.smoker ? 'primary' : 'default'}
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        specialInfo: { ...prev.specialInfo, smoker: true }
                      }))}
                      sx={{ cursor: 'pointer' }}
                    />
                    <Chip
                      label={tr('Hayır', 'No')}
                      color={!formData.specialInfo?.smoker ? 'primary' : 'default'}
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        specialInfo: { ...prev.specialInfo, smoker: false }
                      }))}
                      sx={{ cursor: 'pointer' }}
                    />
                  </Box>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  {tr('İşe Başlama Tarihi (En Erken Ne Zaman İşe Başlayabilirsiniz?)', 'Start Date (Earliest Available Date)')}
                </Typography>
                <DatePicker
                  label={tr('İşe Başlama Tarihi', 'Start Date')}
                  value={formData.jobPreferences?.startDate}
                  onChange={(date) => setFormData(prev => ({
                    ...prev,
                    jobPreferences: { ...prev.jobPreferences, startDate: date }
                  }))}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  {tr('Gece Çalışabilir misiniz?', 'Can You Work at Night?')}
                </Typography>
                <FormControl component="fieldset">
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Chip
                      label={tr('Evet', 'Yes')}
                      color={formData.jobPreferences?.canWorkNightShift ? 'primary' : 'default'}
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        jobPreferences: { ...prev.jobPreferences, canWorkNightShift: true }
                      }))}
                      sx={{ cursor: 'pointer' }}
                    />
                    <Chip
                      label={tr('Hayır', 'No')}
                      color={!formData.jobPreferences?.canWorkNightShift ? 'primary' : 'default'}
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        jobPreferences: { ...prev.jobPreferences, canWorkNightShift: false }
                      }))}
                      sx={{ cursor: 'pointer' }}
                    />
                  </Box>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  {tr('Fazla Mesai Yapabilir misiniz?', 'Can You Work Overtime?')}
                </Typography>
                <FormControl component="fieldset">
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Chip
                      label={tr('Evet', 'Yes')}
                      color={formData.jobPreferences?.canWorkOvertime ? 'primary' : 'default'}
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        jobPreferences: { ...prev.jobPreferences, canWorkOvertime: true }
                      }))}
                      sx={{ cursor: 'pointer' }}
                    />
                    <Chip
                      label={tr('Hayır', 'No')}
                      color={!formData.jobPreferences?.canWorkOvertime ? 'primary' : 'default'}
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        jobPreferences: { ...prev.jobPreferences, canWorkOvertime: false }
                      }))}
                      sx={{ cursor: 'pointer' }}
                    />
                  </Box>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  {tr('Seyahat Edebilir misiniz?', 'Can You Travel?')}
                </Typography>
                <FormControl component="fieldset">
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Chip
                      label={tr('Evet', 'Yes')}
                      color={formData.jobPreferences?.canTravel ? 'primary' : 'default'}
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        jobPreferences: { ...prev.jobPreferences, canTravel: true }
                      }))}
                      sx={{ cursor: 'pointer' }}
                    />
                    <Chip
                      label={tr('Hayır', 'No')}
                      color={!formData.jobPreferences?.canTravel ? 'primary' : 'default'}
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        jobPreferences: { ...prev.jobPreferences, canTravel: false }
                      }))}
                      sx={{ cursor: 'pointer' }}
                    />
                  </Box>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  {tr('Şehir Dışına Tayin Olabilir misiniz?', 'Can You Be Relocated to Another City?')}
                </Typography>
                <FormControl component="fieldset">
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Chip
                      label={tr('Evet', 'Yes')}
                      color={formData.jobPreferences?.canRelocate ? 'primary' : 'default'}
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        jobPreferences: { ...prev.jobPreferences, canRelocate: true }
                      }))}
                      sx={{ cursor: 'pointer' }}
                    />
                    <Chip
                      label={tr('Hayır', 'No')}
                      color={!formData.jobPreferences?.canRelocate ? 'primary' : 'default'}
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        jobPreferences: { ...prev.jobPreferences, canRelocate: false }
                      }))}
                      sx={{ cursor: 'pointer' }}
                    />
                  </Box>
                </FormControl>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* DİĞER BİLGİLER - Hobileriniz, İlgi Alanlarınız */}
        <Accordion expanded={expanded === 'panel10'} onChange={handleAccordionChange('panel10')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <DescriptionIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h6">{tr('Diğer Bilgiler', 'Other Information')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label={tr('Hobileriniz, İlgi Alanlarınız, Sosyal Faaliyetleriniz', 'Hobbies, Interests, and Social Activities')}
                  placeholder={tr('Hobilerinizi ve ilgi alanlarınızı açıklayınız...', 'Please describe your hobbies and interests...')}
                  value={formData.additionalInfo?.hobbiesAndInterests || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    additionalInfo: { ...prev.additionalInfo, hobbiesAndInterests: e.target.value }
                  }))}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label={tr('Eklemek İstediğiniz Diğer Bilgiler', 'Additional Information You Want to Add')}
                  placeholder={tr('CV\'nizde belirtmek istediğiniz diğer önemli bilgileri buraya yazabilirsiniz...', 'You can write other important information you want to include in your CV here...')}
                  value={formData.additionalInfo?.otherInfo || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    additionalInfo: { ...prev.additionalInfo, otherInfo: e.target.value }
                  }))}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Physical Attributes and References (moved from panel10 to panel11) */}
        <Accordion expanded={expanded === 'panel11'} onChange={handleAccordionChange('panel11')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <PersonIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h6">{tr('Fiziksel Özellikler ve Referanslar', 'Physical Attributes and References')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" color="primary" gutterBottom>
                  {tr('Fiziksel Özellikler', 'Physical Attributes')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label={tr('Boy (cm)', 'Height (cm)')}
                  value={formData.physicalAttributes?.height || ''}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    // Boy maksimum 300 cm
                    if (!isNaN(value) && value > 300) {
                      return;
                    }
                    setFormData(prev => ({
                      ...prev,
                      physicalAttributes: { ...prev.physicalAttributes, height: value }
                    }));
                  }}
                  inputProps={{ min: 0, max: 300, step: 1 }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label={tr('Kilo (kg)', 'Weight (kg)')}
                  value={formData.physicalAttributes?.weight || ''}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    // Kilo maksimum 500 kg
                    if (!isNaN(value) && value > 500) {
                      return;
                    }
                    setFormData(prev => ({
                      ...prev,
                      physicalAttributes: { ...prev.physicalAttributes, weight: value }
                    }));
                  }}
                  inputProps={{ min: 0, max: 500, step: 0.1 }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={tr('Ayakkabı Numarası', 'Shoe Size')}
                  value={formData.physicalAttributes?.shoeSize || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    physicalAttributes: { ...prev.physicalAttributes, shoeSize: e.target.value }
                  }))}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={tr('Beden', 'Clothing Size')}
                  value={formData.physicalAttributes?.clothingSize || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    physicalAttributes: { ...prev.physicalAttributes, clothingSize: e.target.value }
                  }))}
                />
              </Grid>

              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="subtitle1" color="primary" gutterBottom>
                  {tr('Referanslar', 'References')}
                </Typography>
              </Grid>
              {formData.references.map((ref, index) => (
                <Grid item xs={12} key={ref.id}>
                  <Card sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="subtitle2" color="primary">
                        {tr('Referans', 'Reference')} #{index + 1}
                      </Typography>
                      <IconButton onClick={() => removeReference(index)} size="small" color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label={tr('Adı - Soyadı', 'Full Name')}
                          value={ref.fullName}
                          onChange={(e) => updateReference(index, 'fullName', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label={tr('Kurumu / Görevi', 'Company / Duty')}
                          value={ref.company}
                          onChange={(e) => updateReference(index, 'company', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label={tr('Telefon Numarası', 'Phone Number')}
                          value={ref.phone}
                          onChange={(e) => updateReference(index, 'phone', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          type="email"
                          label={tr('E-posta', 'Email')}
                          value={ref.email || ''}
                          onChange={(e) => updateReference(index, 'email', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label={tr('Pozisyon', 'Position')}
                          value={ref.position}
                          onChange={(e) => updateReference(index, 'position', e.target.value)}
                        />
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>
              ))}
              <Grid item xs={12}>
                <Button startIcon={<AddIcon />} onClick={addReference} variant="outlined">
                  {tr('Referans Ekle', 'Add Reference')}
                </Button>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* KVKK Consent */}
        <Accordion expanded={expanded === 'panel12'} onChange={handleAccordionChange('panel12')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <DescriptionIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h6">{tr('KVKK Aydınlatma Metni ve Onay', 'PDPL Information Text and Consent')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ p: 2 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2" paragraph>
                  {tr('İşbu formdaki sorulara verdiğim cevapların doğru olduğunu, zaman içerisinde değişecek olan bilgilerimi en geç 10 (on) gün içerisinde yazılı olarak bildireceğimi ve hakikat dışı ve / veya eksik bir beyanımla işe alınmış bulunduğumda tazminata gerek olmaksızın işime son verileceğini ve bundan dolayı her hangi bir takip, talep ve iddiada bulunamayacağımı eksik veya hakikat dışında beyanımla işe alınmamdan dolayı şirketin uğrayabileceği her türlü zarar ve ziyana tazmin edeceğimi kabul ve beyan ederim.', 'I declare that the answers I have provided in this form are accurate, that I will notify any changes in writing within 10 days, and that if I am hired based on incomplete or false statements, my employment may be terminated without compensation. I also agree to compensate any damage caused to the company due to such incomplete or false statements.')}
                </Typography>
              </Alert>

              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  {tr('ÖNEMLİDİR!', 'IMPORTANT!')}
                </Typography>
                <Typography variant="body2" component="div">
                  <ol style={{ paddingLeft: '20px', margin: 0 }}>
                    <li>{tr('İş başvuru formu doldurmuş olmak şirketimizi hiçbir taahhüt altına sokmaz.', 'Submitting a job application form does not place the company under any obligation.')}</li>
                    <li>{tr('Bu formda vereceğiniz bilgiler gizli tutulacak, sadece kişisel değerlendirmelerimizde kullanılacaktır.', 'Information provided in this form will be kept confidential and used only for evaluation purposes.')}</li>
                  </ol>
                </Typography>
              </Alert>

              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2" paragraph>
                  {tr('Yukarıda talep edilen bilgiler; iş başvurunuzun şirketimizce değerlendirilmesine müteakip, sözleşme yapılması halinde sözleşme eki olarak saklanacak, iş başvurunuzun olumsuz sonuçlanması halinde derhal imha edilecektir.', 'The information requested above will be evaluated by our company. If a contract is made, it will be stored as an annex to the contract; if your application is unsuccessful, it will be destroyed immediately.')}
                </Typography>
                <Typography variant="body2">
                  {tr('İş başvuru formunda yer alan ve kişisel bilgilerimi verdiğim aşamasında gerekli aydınlatmanın tarafımdan yapıldığı ve açık rızalarımın yine tarafımdan alındığını, bu konuyla ilgili tüm hukuki sorumluluğun tarafımda olduğunu beyan ve taahhüt ederim.', 'I declare and undertake that the necessary disclosures were made and my explicit consents were obtained during the process of providing personal data in this job application form, and that all legal responsibility regarding this matter belongs to me.')} 
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {tr('Yukarıda vermiş olduğum bilgilerin eksiksiz ve doğru olduğunu, "İş Başvuru Formu"nda yer alan özel nitelikli kişisel verilerim de dahil olmak üzere tüm kişisel verilerimin "İş Başvurusu" kapsamında kullanılmasına, işlenmesine ve saklanmasına açık rızam olduğunu kabul ve beyan ederim.', 'I declare that the information I have provided above is complete and accurate, and I expressly consent to the use, processing, and storage of all my personal data, including sensitive personal data, within the scope of this job application.')}
                </Typography>
              </Alert>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                <input
                  type="checkbox"
                  id="kvkk-consent"
                  required
                  style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    kvkkConsent: e.target.checked
                  }))}
                />
                <label htmlFor="kvkk-consent" style={{ cursor: 'pointer', color: 'white', fontWeight: 'bold' }}>
                  {tr('Yukarıdaki metni okudum, anladım ve kabul ediyorum. *', 'I have read, understood, and accept the text above. *')}
                </label>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Submit Button */}
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2, pb: 2 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              minWidth: { xs: '100%', sm: 250 },
              py: 1.5,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: 3,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #653a8b 100%)',
                boxShadow: 4
              },
              '&:disabled': {
                background: '#ccc'
              }
            }}
          >
            {loading ? tr('Kaydediliyor...', 'Saving...') : tr('CV\'yi Kaydet', 'Save CV')}
          </Button>
        </Box>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
            {snackbar.message}
          </Alert>
        </Snackbar>
        </Box>
      </LocalizationProvider>
    </Box>
  );
};

export default CVApplicationForm;
